import { Dispatch, ReactElement, SetStateAction, useEffect, useState } from "react";
import {
	SharePointImagePreviewDialog,
	SharePointImagePreviewDialogProps
} from "./SharePointImagePreviewDialog";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { BookmarkEvent, subscribe as bookmarkSubscribe } from "client_legacy/Documents/Bookmarks/events";
import { DocumentStoreType } from "client/Api/Documents/common";
import { FileViewModel } from "client/Api/Documents/SharePoint/common";
import { renderReactDialog } from "client/Components";

const SharePointImagePubSubWrapper = (props: SharePointImagePreviewDialogProps): ReactElement => {
	const { allImageItems, ...rest } = props;

	const [allImages, setAllImages] = useState<readonly FileViewModel[]>(allImageItems);

	usePubSubEvents(setAllImages);

	return <SharePointImagePreviewDialog {...rest} allImageItems={allImages} />;
};

function usePubSubEvents(
	setCurrentItems: Dispatch<SetStateAction<readonly FileViewModel[]>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(
				setCurrentItems
			);

			return () => subscriptionTokens.destroy();
		},
		[
			setCurrentItems
		]
	);
}

function registerSubscriptions(setAllImages: Dispatch<SetStateAction<readonly FileViewModel[]>>) {
	const result = new SubscriptionTokenCollection();

	const bookmarkAddedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Added,
		(_, model) => {
			if (model.storeType !== DocumentStoreType.SharePoint) {
				return;
			}

			setAllImages(
				prevState => {
					return prevState.map((image) => {
						if (image.id === model.itemId) {
							image.bookmarkId = model.id;
						}

						return { ...image };
					});
				}
			);
		}
	);

	result.add(bookmarkAddedToken);

	const bookmarkDeletedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Deleted,
		(_, bookmarkIds) => {
			setAllImages(
				prevState => {
					return prevState.map(image => {
						if (image.bookmarkId && bookmarkIds.includes(image.bookmarkId)) {
							image.bookmarkId = null;
						}

						return { ...image };
					});
				}
			);
		}
	);

	result.add(bookmarkDeletedToken);

	return result;
}

SharePointImagePubSubWrapper.displayName = "SharePointImagePubSubWrapper";

export function openImageDialog(
	props: Omit<SharePointImagePreviewDialogProps, "onClose">
) {
	renderReactDialog(
		(onClose) => (
			<SharePointImagePubSubWrapper
				{...props}
				onClose={onClose}
			/>
		)
	);
}
